import {
    Dialog,
    DialogHeader,
    DialogBody,
    Input,
    Avatar,
    IconButton,
    Button,
} from "@material-tailwind/react";
import { useEffect, useState } from "react";
import { XMarkIcon, MagnifyingGlassIcon, ChevronDownIcon, XCircleIcon } from "@heroicons/react/24/solid";
import { useDispatch, useSelector } from "react-redux";
import ScrollToTop from "@/components/ScrollToTop";
import ShowLoader from "@/components/Loader";
import { fetchAssignedMembers } from "@/store/slices/orderSlice";
import { useTranslation } from "react-i18next";

const formatRole = (role) => {
    if (!role) return "";
    return role
        .split("_")
        .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
        .join(" ");
};

const formatPhone = (countryCode, mobile) => {
    if (!mobile) return "—";
    const code = (countryCode || "").trim();
    const num = String(mobile).replace(/^0+/, "");
    return code ? `${code} ${num}` : num;
};

const formatDesignation = (designation) => {
    if (!designation) return "—";
    return designation
        .split("_")
        .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
        .join(" ");
};

export function ViewAssignedMembers({ onClose, rfq_id, order_id }) {
    const dispatch = useDispatch();
    const [search, setSearch] = useState('');
    const { t } = useTranslation('order');

    const { assigned_members, loading } = useSelector((state) => state.order);

    const groups = assigned_members?.groups || [];
    const total = assigned_members?.total ?? 0;

    // useEffect(() => {
    //     if (order_id || rfq_id) {
    //         dispatch(fetchAssignedMembers({
    //             order_id,
    //             rfq_id,
    //             search,
    //         }));
    //     }
    // }, [dispatch, order_id, rfq_id]);

    useEffect(() => {
        if (order_id || rfq_id) {
            dispatch(fetchAssignedMembers({
                order_id,
                rfq_id,
                search,
            }));
        }

        return () => {
            if (order_id || rfq_id) {
                dispatch(fetchAssignedMembers({
                    order_id,
                    rfq_id,
                    search: '', 
                }));
            }
        };
    }, [dispatch, order_id, rfq_id]);


    const handleSearch = (event) => {
        if (event.key === "Enter" && search) {
            dispatch(fetchAssignedMembers({ order_id: order_id, rfq_id: rfq_id, search: search }));
        } else if (event.key === "Backspace" && search.length === 0) {
            dispatch(fetchAssignedMembers({ order_id: order_id, rfq_id: rfq_id, search: '' }));
        }
    };
    const handleSearchClick = () => {
        dispatch(fetchAssignedMembers({ order_id: order_id, rfq_id: rfq_id, search: search }));
    };
    const removeSearch = () => {
        dispatch(fetchAssignedMembers({ order_id: order_id, rfq_id: rfq_id, search: '' }));
        setSearch('');
    }

    return (
        <Dialog
            open={true}
            handler={onClose}
            size="md"
            className="rounded-2xl p-0"
        >

            <DialogHeader className="flex justify-between items-center px-6 py-4 border-b">
                <h2 className="text-2xl font-bold text-gray-900">
                    {t('assigned_members_title', { count: total })}
                </h2>
                <button
                    onClick={onClose}
                    className="p-1 rounded-full text-gray-500 hover:bg-gray-100 transition"
                >
                    <XMarkIcon className="w-6 h-6" />
                </button>
            </DialogHeader>

            <DialogBody className="px-6 max-h-[65vh] overflow-y-auto">
                {/* Search / Add People */}
                <div className="w-full sm:w-auto flex items-center gap-2 mb-4">
                    <div className="w-full relative">

                    <Input
                        label={t('search_label')}
                            value={search}
                            onKeyUp={handleSearch}

                        onChange={(e) => setSearch(e.target.value)}
                        icon={!search && <MagnifyingGlassIcon className="h-5 w-5" />}
                    />

                    {search && (
                        <IconButton
                            variant="text"
                            size="sm"
                            className="!absolute right-1 top-1"
                                onClick={() => removeSearch()}
                        >
                            <XCircleIcon className="h-5 w-5 text-red-500" />
                        </IconButton>
                    )}
                    </div>
                    {search && (
                        <Button
                            variant="outlined"
                            className="flex items-center border-gray-300 capitalize font-bold bg-black text-white whitespace-nowrap"
                            onClick={handleSearchClick}
                        >
                            {t('search_button')}
                        </Button>
                    )}
                </div>

                {loading ? <div className="text-center py-2 text-gray-500"> <div role="status">
                    <svg aria-hidden="true" className="inline w-8 h-8 text-gray-200 animate-spin dark:text-gray-600 fill-green-500" viewBox="0 0 100 101" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z" fill="currentColor" />
                        <path d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z" fill="currentFill" />
                    </svg>
                </div></div> : ''}

                {/* Groups */}
                {groups.length === 0 && !loading && (
                    <div className="text-center py-12 text-gray-500">
                        {t('no_assigned_members_found')}
                    </div>
                )}

                <div className="space-y-6">
                    {groups.map((group, gIdx) => (
                        <div key={gIdx} className="space-y-3">
                            <h3 className="text-base font-semibold text-gray-800">
                                {formatRole(group.role)}
                            </h3>

                            <div className="space-y-2">
                                {(group.members || []).map((member) => (
                                    <div
                                        key={member.id}
                                        className="flex items-center gap-4 py-3 px-4 rounded-lg border border-gray-200 bg-white hover:bg-gray-50/50 transition"
                                    >
                                        <Avatar
                                            src={
                                                member.assigned_to?.profile_image ||
                                                "/img/default-avatar-profile-icon.avif"
                                            }
                                            alt={member.assigned_to?.name}
                                            size="md"
                                            className="shrink-0"
                                            onError={(e) => {
                                                e.target.src =
                                                    "/img/default-avatar-profile-icon.avif";
                                            }}
                                        />
                                        <div className="flex-1 min-w-0">
                                            <h4 className="font-medium text-gray-900">
                                                {member.assigned_to?.name || "—"}
                                            </h4>
                                            <p className="text-sm text-gray-600">
                                                {formatPhone(
                                                    member.assigned_to?.country_code,
                                                    member.assigned_to?.mobile_number
                                                )}
                                            </p>
                                        </div>
                                        <div className="flex items-center gap-1 shrink-0">
                                            <span className="text-sm font-medium text-gray-700">
                                                {formatDesignation(member.assigned_to?.designation) || member.assigned_to?.user_type || "—"}
                                            </span>
                                            <ChevronDownIcon className="w-4 h-4 text-gray-500" />
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    ))}
                </div>
            </DialogBody>
        </Dialog>
    );
}

export default ViewAssignedMembers;
